import { useState, useEffect, useRef, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Button from "react-bootstrap/Button";
import Offcanvas from "react-bootstrap/Offcanvas";
import Form from "react-bootstrap/Form";
import PhoneInput from "react-phone-input-2";
import { toast } from "sonner";
import "react-phone-input-2/lib/style.css";
import "./users.css";
import Select from "react-select";
import { components } from "react-select";
import { checkDuplicate, checkDuplicate_edit, customStyles, useDebounce } from "../../Utils/helpers/commonhelper";
import { InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { GetRequest } from "../../Utils/hooks/ApiHandler";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import { parsePhoneNumber } from "libphonenumber-js/min";
import { baseURL } from "../../Utils/constants/constant";
import { useOutsideClickHandler } from "../../Utils/hooks/useOutsideClickHandler";
// Zod schema for form validation
const idString = (msg) =>
  z.preprocess((val) => {
    if (val == null) return "";
    if (typeof val === "object") {
      if ("value" in val) return String(val.value ?? "");
      return "";
    }
    return String(val);
  }, z.string().min(1, msg));


const validatePhoneNumber = (rawNumber, countryCode) => {
  try {
    if (!rawNumber) return false;
    const phoneNumber = parsePhoneNumber(
      String(rawNumber),
      String(countryCode?.toUpperCase())
    );
    return phoneNumber?.isValid() || false;
  } catch (error) {
    return false;
  }
};
const userSchema = z.object({
  user_id: z.string().optional(),
  salutation: z.enum(["Mr", "Mrs", "Miss"]).optional(),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  phoneType: z.enum(["mobile", "home", "work", "personal"]),
  // countryCode: z.string().min(1, "Country code is required"),
  // phone: z
  //   .string()
  //   .min(10, "Phone must be 10 digits")
  //   .max(10, "Phone must be 10 digits")
  //   .regex(/^\d+$/, "Phone must contain only digits"),
  countryCode: z.string().min(1, "Required"), // Stores ISO code (e.g., "in")
  dialCode: z.string().optional(),            // Stores numeric code (e.g., "91")
  phone: z.string().min(1, "Phone number is required"),
  email: z.string().email("Invalid email"),
  role: z.string().min(1, "Role is required"),
  department: z.string().optional(),
  designation: z.string().optional(),
  team: z.array(z.string()).min(1, "At least one team is required"),
  leadOwnerId: idString("Lead owner is required"),
  status: z.enum(["Active", "Inactive", "unverified"]).default("Active"),
}).superRefine((data, ctx) => {
  if (!validatePhoneNumber(data.phone, data.countryCode)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Invalid phone number for the selected country",
      path: ["phone"],
    });
  }
});

export const UserForm = ({
  show,
  setShowTeamForm,
  setShowUserForm,
  setAddTeam,
  setAlreadySelectedTeam,
  onHide,
  onSubmitForm,
  editUserId,
  initialData,
  roles = [],
  teams = [],
  alreadySelectedTeam = [],
  loading
}) => {

  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });
  const customer_id = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization || "";
  const rf = customer?.token?.authentication || "";
  const extras = { customer, setCustomer, rf, navigate };
  const headers = {
    Authorization: token,
    Authentication: rf,
  };
  const [duplicateEmailMessage, setDuplicateEmailMessage] = useState(null)


  const [leadOwner, setLeadOwner] = useState([]);
  const [selectedLeadOwner, setSelectedLeadOwner] = useState(null);
  // State for selected country, teams, and dropdown visibility
  const [selectedCountry, setSelectedCountry] = useState("in");
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const [unverifiedUserId, setUnverifiedUserId] = useState(null);
  const [departmentList, setDepartment] = useState([]);
  const [designationList, setDesignation] = useState([]);

  const teamDropdownRef = useRef(null);

  // Form setup with react-hook-form and zod validation
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(userSchema),
    defaultValues: {
      salutation: "Mr",
      firstName: "",
      lastName: "",
      phoneType: "mobile",
      countryCode: "in",
      dialCode: "91",
      phone: "",
      email: "",
      role: "",
      department: "",
      designation: "",
      team: [],
    },
  });

  // Reset form when editing or creating new user
  const email = watch("email");
  const debouncedEmail = useDebounce(email || "", 500);

  // Define this outside the component or inside useMemo
  const blankValues = {
    user_id: "",
    salutation: "Mr",
    firstName: "",
    lastName: "",
    phoneType: "mobile",
    countryCode: "91", // or your preferred default
    dialCode: "91",
    phone: "",
    email: "",
    role: "",
    team: [],
    department: "",
    designation: "",
    leadOwnerId: "",
  };

  useEffect(() => {
    if (show) { // Only run logic when offcanvas is shown
      if (editUserId && initialData) {
        const preselectedTeams = Array.isArray(initialData.teams)
          ? initialData.teams.map((id) => id.toString())
          : [];

        setSelectedCountry(initialData.countryCode?.toLowerCase() || "in");
        setSelectedTeams(preselectedTeams);
        reset({
          user_id: initialData.user_id?.toString() || "",
          salutation: initialData.salutation || "Mr",
          firstName: initialData.firstName || "",
          lastName: initialData.lastName || "",
          department: initialData?.department?.toString() || "",
          designation: initialData?.designation?.toString() || "",
          phoneType: initialData.phoneType || "mobile",
          countryCode: initialData.Dailcode?.toLowerCase() || "in",
          dialCode: initialData.countryCode || "91",
          phone: initialData.phone || "",
          email: initialData.email || "",
          role: initialData.role?.toString() || "",
          team: preselectedTeams,
          leadOwnerId: initialData?.assignTo || "",
          status: initialData?.status,
        });
      } else {
        // CLEAR EVERYTHING for Add mode
        setSelectedTeams([]);
        setSelectedCountry("in");
        reset(blankValues);
      }
    }
  }, [editUserId, initialData, show, reset]);

  // Effect for email duplication check
  useEffect(() => {
    const checkEmail = async () => {
      if (debouncedEmail) {
        setDuplicateEmailMessage(null);
        const isEmailExisting = await checkDuplicate_edit({
          col: "email",
          customer_id: "",
          validating_val: debouncedEmail,
          contact_id: "",
          user_id: editUserId,
          checkFor: "register",
          existing_val: editUserId && initialData?.email ? initialData.email : "",
        });

        if (isEmailExisting?.status === "success") {
          setDuplicateEmailMessage("success");
        } else if (isEmailExisting?.status === "unverified") {
          setUnverifiedUserId(isEmailExisting?.data);
          setDuplicateEmailMessage("unverified");
        } else {
          setDuplicateEmailMessage(null);
        }
      } else {
        setDuplicateEmailMessage(null);
      }
    };
    checkEmail();
  }, [debouncedEmail, editUserId, initialData?.email]);
  // Update form value when selectedTeams changes
  useEffect(() => {
    setValue("team", selectedTeams);
  }, [selectedTeams, setValue]);

  useEffect(() => {
    if (Array.isArray(alreadySelectedTeam) && alreadySelectedTeam.length > 0) {
      // Filter out duplicates and add new team IDs
      const newTeams = alreadySelectedTeam.filter(
        (id) => !selectedTeams.includes(id)
      );

      if (newTeams.length > 0) {
        setSelectedTeams((prev) => [...prev, ...newTeams]);
      }
    }
  }, [alreadySelectedTeam, selectedTeams]);

  // Close team dropdown when clicking outside
  useOutsideClickHandler({
    objRef: teamDropdownRef,
    isDropdown: teamDropdownOpen,
    setDropdown: setTeamDropdownOpen
  })

  // Add team to selected teams
  const handleTeamSelect = (teamId) => {
    if (!selectedTeams.includes(teamId)) {
      setSelectedTeams((prev) => [...prev, teamId]);
    }
  };

  // Remove team from selected teams
  const removeTeam = (teamId) => {
    setAlreadySelectedTeam((prev) => prev.filter((id) => id !== teamId));
    setSelectedTeams((prev) => prev.filter((id) => id !== teamId));
  };

  // Handle form submission
  const onSubmit = (data) => {
    if (duplicateEmailMessage) {
      return;
    }
    if (selectedTeams.length === 0) {
      toast.error("At least one team must be selected");
      return;
    }
    onSubmitForm(data);
  };

  // Close form and reset state
  const handleClose = () => {

    reset({
      user_id: "",
      salutation: "Mr",
      firstName: "",
      lastName: "",
      phoneType: "mobile",
      countryCode: "in",
      dialCode: "91",
      phone: "",
      email: "",
      role: "",
      team: [],
      leadOwnerId: "",

      department: "",
      designation: ""
    });
    setSelectedCountry("in");

    setAlreadySelectedTeam([]);
    setSelectedTeams([]);
    setDuplicateEmailMessage(null);
    setUnverifiedUserId(null);
    onHide();
  };

  // Watch country code for phone input
  const countryCode = watch("countryCode");


  // Fetch Owners
  const fetchOwners = async () => {
    const res = await GetRequest(
      `users/users/fetchUserAsOwner/${customer.customer_details.customer_id}`,
      { headers },
      { customer, setCustomer, rf: headers.Authentication, navigate }
    );

    if (res.err) {
      toast.error("Error Fetching Data");
      return;
    }

    const owners = res.data.map((lead) => ({
      leadName: lead.full_name,
      leadId: lead.id,
    }));

    setLeadOwner(owners);

    const currentOwner = getValues("leadOwnerId");
    if (!currentOwner && owners.length > 0) {
      setValue("leadOwnerId", owners[0].leadId, { shouldValidate: true });
      setSelectedLeadOwner({
        value: owners[0].leadId,
        label: owners[0].leadName,
      });
    }
  };

  useEffect(() => {
    fetchOwners();
  }, []);


  const leadOwnerOptions = useMemo(
    () =>
      leadOwner.map((lead) => ({
        value: lead.leadId,
        label: lead.leadName,
      })),
    [leadOwner]
  );

  useEffect(() => {
    if (!show) return;

    const current = getValues("leadOwnerId");

    if (!editUserId && !current && leadOwnerOptions.length > 0) {
      setValue("leadOwnerId", leadOwnerOptions[0].value, {
        shouldValidate: true,
      });

      setSelectedLeadOwner(leadOwnerOptions[0]);
    }
  }, [show, leadOwnerOptions, editUserId, getValues, setValue]);

  const TeamMenuList = (props) => {
    return (
      <>
        <components.MenuList {...props}>
          {props.children}

          {/* Add New Team Button */}
          <div
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              borderTop: "1px solid #eee",
              color: "#007bff",
              fontWeight: "bold"
            }}
            onClick={() => {
              props.selectProps.onAddTeam();   // Custom callback
            }}
          >
            + Add Team
          </div>
        </components.MenuList>
      </>
    );
  };

  const fetchDepartment = async () => {
    try {
      const res = await GetRequest(`${baseURL}common/fetchDepartment/${customer_id}`, { headers }, extras)
      if (res?.status === "fail" || res?.status !== "success") {
        return console.error(res?.message)
      }
      setDepartment(res?.data)
    } catch (error) {
      console.error(error)
    }
  }
  const fetchDesignation = async () => {
    try {
      const res = await GetRequest(`${baseURL}common/fetchDesignation/${customer_id}`, { headers }, extras)
      if (res?.status === "fail" || res?.status !== "success") {
        return console.error(res?.message)
      }
      setDesignation(res?.data)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchDepartment()
    fetchDesignation()
  }, [])
  return (
    <Offcanvas
      show={show}
      className="w-50"
      onHide={handleClose}
      placement="end"
      backdrop="static"
      scroll
    // style={{'width': '50%'}}
    >
      <Offcanvas.Header
        closeButton
        className="border-bottom border-dashed pt-3"
      >
        <div className="d-flex align-items-center gap-2">
          <div className="pt-0 ps-4">
            <Offcanvas.Title
              style={{ color: "#0791ff" }}
              className="addEnquiryHead"
            >  {editUserId ? "Edit User" : "Add User"}
            </Offcanvas.Title>
            <p
              style={{ fontSize: "14px", color: "#b26792", lineHeight: 1.1 }}
              className=" text-secondary text-nowrap mb-0"
            >
              {/* Create New Contact For Future */}
            </p>
          </div>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body className="d-flex flex-column">
        <Form onSubmit={handleSubmit(onSubmit)} className="form-container">
          <div className="row g-3">

            <Form.Group className="col-md-2 mb-3 focus-z-index position-relative userForm-floating-label">
              <Form.Label>Salutation</Form.Label>

              <Controller
                name="salutation"
                control={control}
                defaultValue="Mr"
                render={({ field }) => (
                  <Select
                    {...field}
                    classNamePrefix="react-select"
                    styles={customStyles}
                    isClearable={false}
                    placeholder="Select Salutation"
                    options={[
                      { value: "Mr", label: "Mr" },
                      { value: "Mrs", label: "Mrs" },
                      { value: "Miss", label: "Miss" },
                    ]}
                    value={[
                      { value: "Mr", label: "Mr" },
                      { value: "Mrs", label: "Mrs" },
                      { value: "Miss", label: "Miss" },
                    ].find((opt) => opt.value === field.value)}
                    onChange={(option) => field.onChange(option?.value)}
                  />
                )}
              />

              {errors.salutation && (
                <small className="text-danger">
                  {errors.salutation.message}
                </small>
              )}
            </Form.Group>
            <Form.Group className="col-md-4 focus-z-index position-relative userForm-floating-label">
              <Form.Label>
                First Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                {...register("firstName")}
                placeholder="First Name"
                className="form-control"
              />
              {errors.firstName && (
                <p className="text-danger mt-1" style={{ fontSize: "12px" }}>

                  {errors.firstName.message}
                </p>
              )}
            </Form.Group>
            <Form.Group className="col-md-6 focus-z-index position-relative userForm-floating-label">
              <Form.Label>
                Last Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                {...register("lastName")}
                placeholder="Last Name"
                className="form-control"
              />
              {errors.lastName && (
                <p className="text-danger mt-1" style={{ fontSize: "12px" }}>

                  {errors.lastName.message}
                </p>
              )}
            </Form.Group>
          </div>
          <div className="row mb-3 g-3">
            <Form.Group className="col-md-2 focus-z-index position-relative userForm-floating-label">
              <Form.Label>Phone Type</Form.Label>

              {/* <Select
                {...register("phoneType")}
                classNamePrefix="react-select"
                styles={customStyles}
                placeholder="Select Type"
                options={[
                  { value: "mobile", label: "Mobile" },
                  { value: "home", label: "Home" },
                  { value: "work", label: "Work" },
                  { value: "personal", label: "Personal" },
                ]}
                onChange={(selected) => {
                  setValue("phoneType", selected?.value, { shouldValidate: true });
                }}
              /> */}
              <Select
                classNamePrefix="react-select"
                placeholder="Select Type"
                styles={customStyles}
                options={[
                  { value: "mobile", label: "Mobile" },
                  { value: "home", label: "Home" },
                  { value: "work", label: "Work" },
                  { value: "personal", label: "Personal" },
                ]}

                value={[
                  { value: "mobile", label: "Mobile" },
                  { value: "home", label: "Home" },
                  { value: "work", label: "Work" },
                  { value: "personal", label: "Personal" },
                ].find((opt) => opt.value === watch("phoneType"))}

                onChange={(selected) => {
                  setValue("phoneType", selected?.value, { shouldValidate: true });
                }}
              />

              {errors.phoneType && (
                <p className="text-danger mt-1" style={{ fontSize: "12px" }}>

                  {errors.phoneType.message}
                </p>)}
            </Form.Group>

            <div className="col-md-4 mb-3">
              <InputGroup style={{
                position: 'relative',
                display: 'block',
                flexWrap: 'wrap',
                alignItems: 'stretch',
                width: '100%'
              }}>
                <Form.Label>
                  Phone <span className="text-danger">*</span>
                </Form.Label>
                <div className="my-phone-input"><Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      country={selectedCountry}
                      countryCodeEditable={false}
                      buttonStyle={{ border: "none" }}
                      // Construct display value
                      value={`${watch("dialCode")}${field.value}`}
                      containerStyle={{
                        border: errors.phone ? "1px solid #dc3545" : "1px solid #c5c5c5",
                        borderRadius: "5px",
                      }}
                      onChange={(value, country, e, rawInputValue) => {
                        const dial = String(country?.dialCode || "");
                        const iso = String(country?.countryCode || "");

                        const rawNum = rawInputValue?.replace(/\D/g, "") || "";
                        const nationalNumber = dial
                          ? rawNum.replace(new RegExp("^" + dial), "")
                          : rawNum;

                        // ISO code for validation
                        setValue("countryCode", iso, { shouldValidate: true });

                        // numeric code for API
                        setValue("dialCode", dial);

                        field.onChange(nationalNumber);

                        setSelectedCountry(iso || "in");
                      }}
                      inputStyle={{
                        width: "100%",
                        height: "52px",
                        border: "none",
                      }}
                    />
                  )}
                />
                </div>
              </InputGroup>

              {/* Validation Error */}
              {errors.phone && (
                <p className="text-danger mt-1" style={{ fontSize: "12px" }}>

                  {errors.phone.message}
                </p>
              )}

              {/* Hidden CountryCode Field */}
              <input type="hidden" {...register("countryCode")} />

              {/* Register phone so react-hook-form can validate it */}
              <input type="hidden" {...register("phone")} />
            </div>

            {/* </Form.Group> */}
            {/* <Form.Group className="col-md-6 focus-z-index position-relative userForm-floating-label">
              <Form.Label>
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                {...register("email")}
                placeholder="abc@gmail.com"
                className="form-control"
              />
              {errors.email && (
                <p className="text-danger mt-1" style={{ fontSize: "12px" }}>

                  {errors.email.message}
                </p>
              )}
            </Form.Group> */}
            <Form.Group className="col-md-6 focus-z-index position-relative userForm-floating-label">
              <Form.Label
                style={{ marginBottom: 1 }}
                className=" fw-lighter  "
              >
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control type="email" {...register("email")} />
              {duplicateEmailMessage && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "12px",
                    gap: 2,
                    paddingTop: "4px",
                    marginBottom: "10px",
                  }}
                  className="text-danger"
                >
                  {/* <IconCircleX
                                            stroke={2}
                                            style={{ width: "14px", height: "14px" }}
                                          /> */}
                  {/* {duplicateEmailMessage ? "sucu"} */}
                  {duplicateEmailMessage === "success" ? (
                    "Email already exist!"
                  ) : (
                    <>
                      <span>
                        Email already exist.
                      </span>

                    </>
                  )}
                </div>
              )}
              <small className="text-danger">
                {errors.email?.message}
              </small>
            </Form.Group>
          </div>

          <div className="row mb-3  g-3">

            <Form.Group className="col-md-6 focus-z-index position-relative userForm-floating-label">
              <Form.Label>
                Role <span className="text-danger">*</span>
              </Form.Label>

              <Select
                classNamePrefix="react-select"
                placeholder="Select Role"

                styles={customStyles}
                options={roles.map((r) => ({
                  value: r.id,
                  label: r.role_title,
                }))}
                value={
                  roles
                    .map((r) => ({ value: r.id, label: r.role_title }))
                    .find((opt) => opt.value === watch("role")) || null
                }
                onChange={(selected) => {
                  setValue("role", selected?.value || "", {
                    shouldValidate: true,
                  });
                }}
              />
              {/* Validation Error */}
              {errors.role && (
                <p className="text-danger mt-1" style={{ fontSize: "12px" }}>
                  {errors.role.message}</p>
              )}

              {/* register hidden field for RHF */}
              <input type="hidden" {...register("role")} />
            </Form.Group>

            <Form.Group
              className="col-md-6 mb-3 focus-z-index position-relative userForm-floating-label"
              controlId="formOwner"
            >
              <Form.Label style={{ marginBottom: 1 }}>
                Reporting To{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <Controller
                name="leadOwnerId"
                control={control}
                rules={{ required: "Owner is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={leadOwnerOptions}
                    placeholder="Select Owner"
                    classNamePrefix="react-select"
                    styles={customStyles}
                    value={
                      leadOwnerOptions.find(
                        (opt) => opt.value === field.value
                      ) || selectedLeadOwner
                    }
                    onChange={(selected) => field.onChange(selected?.value)}
                  />
                )}
              />

              {errors.leadOwnerId && (
                <p
                  className="text-danger mt-1"
                  style={{ fontSize: "12px" }}
                >
                  {errors.leadOwnerId.message}
                </p>
              )}
            </Form.Group>
          </div>
          <div className="row mb-3  g-3">
            <Form.Group className="col-md-6 focus-z-index position-relative userForm-floating-label">
              <Form.Label>
                Department
              </Form.Label>

              <Select
                classNamePrefix="react-select"
                placeholder="Select Role"

                styles={customStyles}
                options={departmentList?.map((d) => ({
                  value: d.id,
                  label: d.department_name,
                }))}
                value={
                  departmentList
                    .map((d) => ({ value: d.id, label: d.department_name }))
                    .find((opt) => opt.value === watch("department")) || null
                }
                onChange={(selected) => {
                  setValue("department", selected?.value || "", {
                    shouldValidate: true,
                  });
                }}
              />
              {/* Validation Error */}
              {errors.department && (
                <p className="text-danger mt-1" style={{ fontSize: "12px" }}>
                  {errors.department.message}</p>
              )}

              {/* register hidden field for RHF */}
              <input type="hidden" {...register("role")} />
            </Form.Group>
            <Form.Group className="col-md-6 focus-z-index position-relative userForm-floating-label">
              <Form.Label>
                Designation
              </Form.Label>

              <Select
                classNamePrefix="react-select"
                placeholder="Select Role"

                styles={customStyles}
                options={designationList.map((d) => ({
                  value: d.id,
                  label: d.designation_name,
                }))}
                value={
                  designationList
                    .map((d) => ({ value: d.id, label: d.designation_name }))
                    .find((opt) => opt.value === watch("designation")) || null
                }
                onChange={(selected) => {
                  setValue("designation", selected?.value || "", {
                    shouldValidate: true,
                  });
                }}
              />
              {/* Validation Error */}
              {errors.designation && (
                <p className="text-danger mt-1" style={{ fontSize: "12px" }}>
                  {errors.designation.message}</p>
              )}

              {/* register hidden field for RHF */}
              <input type="hidden" {...register("role")} />
            </Form.Group>
            {editUserId && (
              // <div className="row">
              <Form.Group className="col-md-6 focus-z-index position-relative userForm-floating-label mb-3">
                <Form.Label>
                  Status <span className="text-danger">*</span>
                </Form.Label>

                <Select
                  classNamePrefix="react-select"
                  placeholder="Select Status"
                  isDisabled={initialData?.status === "unverified"}
                  styles={customStyles}
                  options={[
                    { value: "Active", label: "Active" },
                    { value: "Inactive", label: "Inactive" },
                    ...(initialData?.status === "unverified"
                      ? [{ value: "unverified", label: "Unverified" }]
                      : []),
                  ]}
                  value={
                    [
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                      { value: "unverified", label: "Unverified" },
                    ].find((opt) => opt.value === watch("status")) || null
                  }
                  onChange={(selected) => {
                    setValue("status", selected?.value || "Active", {
                      shouldValidate: true,
                    });
                  }}
                />

                {/* Validation Error */}
                {errors.status && (
                  <p className="text-danger mt-1" style={{ fontSize: "12px" }}>
                    {errors.status.message}
                  </p>
                )}

                {/* Hidden field for RHF */}
                <input type="hidden" {...register("status")} />
              </Form.Group>

            )}
          </div>


          <div className="row">
            <Form.Group className="col-md-12 focus-z-index position-relative userForm-floating-label">
              <Form.Label>
                Team <span className="text-danger">*</span>
              </Form.Label>

              <Select
                isMulti
                styles={customStyles}
                classNamePrefix="react-select"
                placeholder="Select Teams"
                options={teams.map((t) => ({
                  value: t.id.toString(),
                  label: t.team_name,
                }))}

                value={selectedTeams.map((id) => {
                  const team = teams.find((t) => t.id.toString() === id);
                  return { value: id, label: team?.team_name };
                })}

                onChange={(selectedOptions) => {
                  setSelectedTeams(selectedOptions.map((s) => s.value));
                }}

                components={{ MenuList: TeamMenuList }}

                onAddTeam={() => {
                  setShowTeamForm(true);
                  setShowUserForm(false);
                  setAddTeam(true);
                }}
              />


              {errors.team && (
                <p className="text-danger mt-1" style={{ fontSize: "12px" }}>
                  {errors.team.message}</p>
              )}
            </Form.Group>
          </div>


          {/* <div className="d-flex justify-content-end gap-2 mt-auto fixed-bottom mb-3 me-3">
            <Button
              type="button"
              className="btn-default-outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button type="submit" className="btn-default" disabled={duplicateEmailMessage}>
              {editUserId ? "Update" : "Add"} User
            </Button>
          </div> */}
          <div className="d-flex justify-content-end gap-2 mt-auto fixed-bottom mb-4 me-3">
            <Button type="button" className="btn btn-default-outline" onClick={handleClose}>Cancel</Button>
            <Button
              type="submit"
              className="btn btn-default"
              disabled={loading || Boolean(duplicateEmailMessage)}
            >
              {loading ? <div className="spinner-border spinner-border-sm" role="status" /> : editUserId ? "Update" : "Add"} User
            </Button>
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas >
  );
};